import { useUser } from '@clerk/clerk-react';
import { loadGtag, onGtagScriptLoad, trackGtagSignUp } from '@repo/ui/lib/gtag';
import { useEffect } from 'react';
import { GA_MEASUREMENT_ID } from '@/lib/analytics/ga-measurement-id';
import { isRecentSignup, runWithSignupLock, sendSignupOnce, signUpMethod, signupRetryDelayMs } from '@/lib/analytics/ga-signup';
import { useCookieConsentStore } from '@/stores/use-cookie-consent-store';

/**
 * Loads Google Analytics 4 once this browser accepts analytics cookies, the same
 * gate PostHog uses. Rejecting goes through `stopAnalytics()` in `./consent`,
 * which disables gtag and clears the `_ga*` cookies.
 *
 * A new Clerk account also sends the recommended `sign_up` event, once per
 * browser. A Web Lock plus localStorage keeps two tabs from both sending it.
 * The flag is written only after gtag.js has loaded and collection is still on.
 * OAuth waits until Clerk has a provider, so an empty `externalAccounts` list
 * is not recorded as email. `method` comes from `GA_SIGN_UP_METHODS`. Logins
 * do not: the account has to have been created in the last few minutes. When
 * `VITE_GA_MEASUREMENT_ID` is unset (local dev), nothing loads.
 */
export function GoogleAnalytics() {
    const analyticsAllowed = useCookieConsentStore((state) => state.analytics === true);
    const { isLoaded, isSignedIn, user } = useUser();

    useEffect(() => {
        if (!GA_MEASUREMENT_ID || !analyticsAllowed) return;
        loadGtag(GA_MEASUREMENT_ID);
    }, [analyticsAllowed]);

    useEffect(() => {
        if (!GA_MEASUREMENT_ID || !analyticsAllowed || !isLoaded || !isSignedIn || !user?.createdAt) return;
        const measurementId = GA_MEASUREMENT_ID;
        const userId = user.id;
        const createdAtMs = user.createdAt.getTime();
        const method = signUpMethod(user.externalAccounts[0]?.provider, user.passwordEnabled);
        if (!method) return;
        const storage = typeof localStorage === 'undefined' ? null : localStorage;
        const attempt = { cancelled: false };

        const send = () => {
            if (attempt.cancelled || !isRecentSignup(createdAtMs, Date.now())) return;
            runWithSignupLock(userId, () => {
                if (attempt.cancelled || !isRecentSignup(createdAtMs, Date.now())) return;
                sendSignupOnce(userId, storage, () => trackGtagSignUp(measurementId, method));
            });
        };

        const delay = signupRetryDelayMs(createdAtMs, Date.now());
        const retryTimer = delay === null ? undefined : window.setTimeout(send, delay);
        send();
        const stopListening = onGtagScriptLoad(measurementId, send);

        return () => {
            attempt.cancelled = true;
            if (retryTimer !== undefined) window.clearTimeout(retryTimer);
            stopListening();
        };
    }, [analyticsAllowed, isLoaded, isSignedIn, user]);

    return null;
}
